import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { deleteAuthDevices, getAuthDevices, getAuthMe, postAuthPasswordChange, postAuthPinChange, postAuthPinReset, postAuthPinResetRequest, postAuthPinSet, putAuthMe } from '@/api/auth';
import { getNotificationsPreferences, putNotificationsPreferences } from '@/api/common';
import { Banner, Input, QueryState, Select } from '@/components/kit/ui';
import { askConfirm, askPrompt } from '@/components/ui/ConfirmHost';
import { biometricAvailable, biometricError, disableBiometric, enrolBiometric, enrolledHere } from '@/lib/biometric';
import { isNativeApp } from '@/lib/native';
import { errMsg, fmtDateTime, unwrap } from '@/components/kit/data';
import { useAuth } from '@/stores/auth';

interface Me {
  user: { id: number; name: string; first_name: string | null; last_name: string | null; email: string | null; phone: string | null; username: string | null; locale: string | null; role: string };
  school: { name: string } | null;
  flags: { pin_set: boolean; must_change_password: boolean; phone_verified: boolean };
}
interface Pref { type: string; label: string; in_app: boolean; push: boolean; sms: boolean; email: boolean }
interface Device { id: number | string; device_uuid?: string | null; device_name?: string | null; platform?: string | null; app_version?: string | null; last_seen_at?: string | null }
type Msg = { tone: 'success' | 'error'; text: string } | null;
type SetMsg = (m: Msg) => void;

const meKey = ['auth-me'];
const pinDigits = (v: string) => v.replace(/\D/g, '').slice(0, 4);

function PinInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return <Input label={label} type="password" inputMode="numeric" maxLength={4} className="tracking-[0.5em] text-center" value={value} onChange={(e) => onChange(pinDigits(e.target.value))} />;
}

function Profile({ me, setMsg }: { me: Me; setMsg: SetMsg }) {
  const qc = useQueryClient();
  const u = me.user;
  const realEmail = u.email && u.email.includes('@') ? u.email : '';
  const [f, setF] = useState({ first_name: u.first_name ?? '', last_name: u.last_name ?? '', email: realEmail, locale: u.locale ?? 'en' });
  const emailBad = f.email.trim() !== '' && !/^\S+@\S+\.\S+$/.test(f.email.trim());
  const isStudent = u.role === 'student';
  const [dirty, setDirty] = useState(false);
  const set = (k: keyof typeof f, v: string) => { setF({ ...f, [k]: v }); setDirty(true); };
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { first_name: f.first_name.trim(), last_name: f.last_name.trim(), locale: f.locale };
      if (f.email.trim() !== realEmail) body.email = f.email.trim() || null; // only when it was really changed
      return putAuthMe(body);
    },
    onSuccess: (r) => {
      const nu = unwrap<{ name: string }>(r);
      const s = useAuth.getState();
      if (s.user && s.token) s.setSession({ ...s.user, name: nu.name }, s.token);
      setDirty(false); setMsg({ tone: 'success', text: 'Profile updated.' }); qc.invalidateQueries({ queryKey: meKey });
    },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });
  return (
    <Card title="Profile">
      <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 mb-4 max-w-2xl text-sm">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">You sign in with</p>
        <p className="font-semibold text-slate-900 font-mono break-all">{isStudent ? (u.username ?? '—') : ([u.phone, u.email].filter(Boolean).join('  or  ') || u.username || '—')}</p>
        <p className="text-xs text-slate-500">{isStudent ? 'Your registration (admission) number.' : 'Your phone number or email.'}{(u.phone || realEmail) && isStudent ? ' Your phone or email also works if you add one.' : ''}{me.school ? ` · ${me.school.name}` : ''}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
        <Input label="First name" autoComplete="given-name" value={f.first_name} onChange={(e) => set('first_name', e.target.value)} maxLength={60} />
        <Input label="Last name" autoComplete="family-name" value={f.last_name} onChange={(e) => set('last_name', e.target.value)} maxLength={60} />
        <Input label="Email (optional)" type="email" autoComplete="off" name="contact-email" placeholder={isStudent ? 'Only if you have one' : 'you@example.com'} error={emailBad ? 'Enter a real email address, or leave it empty.' : undefined} value={f.email} onChange={(e) => set('email', e.target.value)} />
        <Select label="Language" value={f.locale} onChange={(e) => set('locale', e.target.value)}>
          <option value="en">English</option><option value="ha">Hausa</option><option value="yo">Yoruba</option><option value="ig">Igbo</option>
        </Select>
      </div>
      <p className="text-sm text-slate-500 mt-3">Phone: {u.phone || 'not added'}{!isStudent && u.username ? ` · Username: ${u.username}` : ''}{u.phone ? '' : u.role === 'owner' ? ' — add it in Staff.' : ' — ask the school owner to add one in Staff.'}</p>
      <Button className="mt-3" disabled={!dirty || save.isPending || emailBad || !f.first_name.trim() || !f.last_name.trim()} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save changes'}</Button>
    </Card>
  );
}

export function PasswordCard({ setMsg, onDone, title = 'Change password' }: { setMsg: SetMsg; onDone?: () => void; title?: string }) {
  const [v, setV] = useState({ current_password: '', password: '', password_confirmation: '' });
  const save = useMutation({
    mutationFn: () => postAuthPasswordChange(v),
    onSuccess: () => { setMsg({ tone: 'success', text: 'Password changed. Other devices were signed out.' }); setV({ current_password: '', password: '', password_confirmation: '' }); onDone?.(); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });
  const mismatch = !!v.password_confirmation && v.password !== v.password_confirmation;
  return (
    <Card title={title}>
      <div className="grid sm:grid-cols-3 gap-3 max-w-3xl">
        <Input label="Current password" type="password" autoComplete="current-password" value={v.current_password} onChange={(e) => setV({ ...v, current_password: e.target.value })} />
        <Input label="New password" hint="At least 8 characters" type="password" autoComplete="new-password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} />
        <Input label="Confirm new password" type="password" autoComplete="new-password" error={mismatch ? 'Passwords do not match.' : undefined} value={v.password_confirmation} onChange={(e) => setV({ ...v, password_confirmation: e.target.value })} />
      </div>
      <Button className="mt-3" disabled={save.isPending || !v.current_password || v.password.length < 8 || v.password !== v.password_confirmation} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Change password'}</Button>
    </Card>
  );
}

function Pin({ pinSet, setMsg }: { pinSet: boolean; setMsg: SetMsg }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'change' | 'reset'>('change');
  const [cur, setCur] = useState('');
  const [pin, setPin] = useState('');
  const [conf, setConf] = useState('');
  const [code, setCode] = useState('');
  const done = (text: string) => { setMsg({ tone: 'success', text }); setCur(''); setPin(''); setConf(''); setCode(''); qc.invalidateQueries({ queryKey: meKey }); };
  const fail = (e: unknown) => setMsg({ tone: 'error', text: errMsg(e) });
  const set = useMutation({ mutationFn: () => postAuthPinSet({ pin, pin_confirmation: conf }), onSuccess: () => done('PIN set.'), onError: fail });
  const change = useMutation({ mutationFn: () => postAuthPinChange({ current_pin: cur, new_pin: pin, new_pin_confirmation: conf }), onSuccess: () => done('PIN changed.'), onError: fail });
  const reqReset = useMutation({ mutationFn: () => postAuthPinResetRequest(), onSuccess: () => setMsg({ tone: 'success', text: 'A reset code has been sent to your phone.' }), onError: fail });
  const reset = useMutation({ mutationFn: () => postAuthPinReset({ code, new_pin: pin, new_pin_confirmation: conf }), onSuccess: () => done('PIN reset.'), onError: fail });
  const ok = pin.length === 4 && pin === conf;

  return (
    <Card title="Security PIN">
      {!pinSet ? (
        <>
          <p className="text-sm text-slate-500 mb-3">A 4-digit PIN confirms sensitive actions such as payments and approvals.</p>
          <div className="flex gap-3 items-end flex-wrap"><div className="w-32"><PinInput label="PIN" value={pin} onChange={setPin} /></div><div className="w-32"><PinInput label="Confirm" value={conf} onChange={setConf} /></div><Button disabled={!ok || set.isPending} onClick={() => set.mutate()}>Set PIN</Button></div>
        </>
      ) : (
        <>
          <div className="flex gap-2 mb-3"><Button variant={mode === 'change' ? 'primary' : 'ghost'} onClick={() => setMode('change')}>Change PIN</Button><Button variant={mode === 'reset' ? 'primary' : 'ghost'} onClick={() => setMode('reset')}>Forgot PIN</Button></div>
          {mode === 'change' ? (
            <div className="flex gap-3 items-end flex-wrap"><div className="w-32"><PinInput label="Current PIN" value={cur} onChange={setCur} /></div><div className="w-32"><PinInput label="New PIN" value={pin} onChange={setPin} /></div><div className="w-32"><PinInput label="Confirm" value={conf} onChange={setConf} /></div><Button disabled={!ok || cur.length < 4 || change.isPending} onClick={() => change.mutate()}>Change PIN</Button></div>
          ) : (
            <div className="space-y-3">
              <Button variant="ghost" disabled={reqReset.isPending} onClick={() => reqReset.mutate()}>{reqReset.isPending ? 'Sending…' : 'Send reset code'}</Button>
              <div className="flex gap-3 items-end flex-wrap"><div className="w-36"><Input label="6-digit code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></div><div className="w-32"><PinInput label="New PIN" value={pin} onChange={setPin} /></div><div className="w-32"><PinInput label="Confirm" value={conf} onChange={setConf} /></div><Button disabled={!ok || code.length !== 6 || reset.isPending} onClick={() => reset.mutate()}>Reset PIN</Button></div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function Preferences({ setMsg }: { setMsg: SetMsg }) {
  const q = useQuery({ queryKey: ['notif-prefs'], queryFn: () => getNotificationsPreferences().then((r) => unwrap<Pref[]>(r)) });
  const [edit, setEdit] = useState<Pref[] | null>(null);
  const rows = edit ?? q.data ?? [];
  const save = useMutation({
    mutationFn: () => putNotificationsPreferences({ preferences: rows.map(({ type, in_app, push, sms, email }) => ({ type, in_app, push, sms, email })) }),
    onSuccess: () => { setMsg({ tone: 'success', text: 'Notification preferences saved.' }); setEdit(null); q.refetch(); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });
  const flip = (type: string, k: 'in_app' | 'push' | 'sms' | 'email') => setEdit(rows.map((r) => (r.type === type ? { ...r, [k]: !r[k] } : r)));
  return (
    <Card title="Notification preferences">
      <QueryState q={q} isEmpty={!rows.length} empty="No notification types to configure.">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">Type</th><th>In-app</th><th>Push</th><th>SMS</th><th>Email</th></tr></thead>
            <tbody>{rows.map((r) => <tr key={r.type} className="border-b last:border-0"><td className="py-2">{r.label}</td>{(['in_app', 'push', 'sms', 'email'] as const).map((k) => <td key={k}><input type="checkbox" aria-label={`${r.label} ${k}`} checked={r[k]} onChange={() => flip(r.type, k)} /></td>)}</tr>)}</tbody>
          </table>
        </div>
        <Button className="mt-3" disabled={!edit || save.isPending} onClick={() => save.mutate()}>Save preferences</Button>
      </QueryState>
    </Card>
  );
}

function Devices({ setMsg }: { setMsg: SetMsg }) {
  const q = useQuery({ queryKey: ['auth-devices'], queryFn: () => getAuthDevices().then((r) => unwrap<Device[]>(r)) });
  const del = useMutation({
    mutationFn: (id: number | string) => deleteAuthDevices(id),
    onSuccess: () => { setMsg({ tone: 'success', text: 'Device signed out.' }); q.refetch(); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });
  return (
    <Card title="Signed-in devices">
      <QueryState q={q} isEmpty={!q.data?.length} empty="No registered devices.">
        <ul className="divide-y text-sm">
          {(q.data ?? []).map((d) => (
            <li key={d.id} className="py-2 flex justify-between items-center gap-3">
              <span>{d.device_name || d.platform || `Device ${d.id}`}{d.platform ? <span className="text-slate-500"> · {d.platform}</span> : null}{d.device_uuid && d.device_uuid === localStorage.getItem('ojp-device') ? <b className="ml-2 text-xs text-green-700">this browser</b> : null} <span className="text-slate-500">· last seen {fmtDateTime(d.last_seen_at ?? null)}</span></span>
              <Button variant="ghost" disabled={del.isPending} onClick={async () => { if (await askConfirm('Sign this device out?')) del.mutate(d.id); }}>Remove</Button>
            </li>
          ))}
        </ul>
      </QueryState>
    </Card>
  );
}

const ROLE_LABEL: Record<string, string> = { owner: 'School owner', desk: 'Desk officer', bursar: 'Bursar', teacher: 'Teacher', parent: 'Parent', student: 'Student' };

/** Account settings for any signed-in role: profile, password, PIN, notification preferences, devices — in tabs. */
/** Phone app only: turn fingerprint sign-in on or off for this phone. */
export function FingerprintCard({ setMsg, name, role }: { setMsg: SetMsg; name: string; role?: string }) {
  const [on, setOn] = useState(!!enrolledHere());
  const [busy, setBusy] = useState(false);
  if (!isNativeApp) return null;
  const turnOn = async () => {
    setBusy(true);
    try {
      if (!(await biometricAvailable())) {
        setMsg({ tone: 'error', text: 'No fingerprint is saved on this phone. Add one in the phone settings (Security → Biometrics), then try again.' });
        return;
      }
      const password = await askPrompt('Enter your password to turn on fingerprint sign-in for this phone.', { title: 'Fingerprint sign-in', placeholder: 'Password', secret: true, confirmLabel: 'Continue' });
      if (!password) return;
      // the phone now asks for the fingerprint to confirm
      await enrolBiometric({ name, role }, { password });
      setOn(true);
      setMsg({ tone: 'success', text: 'Fingerprint sign-in is on. Next time, sign in with your fingerprint.' });
    } catch (e: any) {
      const text = biometricError(e);
      if (text) setMsg({ tone: 'error', text });
    } finally {
      setBusy(false);
    }
  };
  const turnOff = async () => {
    if (!(await askConfirm('Turn off fingerprint sign-in on this phone? You will sign in with your password.', { title: 'Fingerprint sign-in', confirmLabel: 'Turn off' }))) return;
    setBusy(true);
    await disableBiometric(true);
    setOn(false); setBusy(false);
    setMsg({ tone: 'success', text: 'Fingerprint sign-in is off for this phone.' });
  };
  return (
    <Card title="Fingerprint sign-in">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600">{on ? 'On for this phone. Payments and withdrawals still ask for your PIN.' : 'Sign in with your fingerprint instead of typing your password.'}</p>
        {on
          ? <Button variant="ghost" className="shrink-0" onClick={turnOff} disabled={busy}>Turn off</Button>
          : <Button className="shrink-0 whitespace-nowrap" onClick={turnOn} disabled={busy}>{busy ? 'Please wait…' : 'Turn on'}</Button>}
      </div>
    </Card>
  );
}

export function AccountPage({ showPin = true, extra }: { showPin?: boolean; extra?: ReactNode }) {
  const [msg, setMsg] = useState<Msg>(null);
  const [tab, setTab] = useState<'profile' | 'security' | 'alerts' | 'devices'>('profile');
  const me = useQuery({ queryKey: meKey, queryFn: () => getAuthMe().then((r) => unwrap<Me>(r)) });
  const u = me.data?.user;
  const initials = (u?.name ?? '').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  const TABS = [['profile', 'Profile'], ['security', showPin ? 'Password & PIN' : 'Password'], ['alerts', 'Notifications'], ['devices', 'Devices']] as const;
  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {u && (
        <div className="bg-[#0A2540] text-white rounded-3xl p-5 flex items-center gap-4">
          <span className="w-14 h-14 rounded-2xl bg-white/10 grid place-items-center text-xl font-extrabold shrink-0">{initials || '?'}</span>
          <div className="min-w-0 flex-1">
            <p className="text-xl font-extrabold truncate">{u.name}</p>
            <p className="text-sm text-white/70 truncate">{ROLE_LABEL[u.role] ?? u.role}{me.data?.school ? ` · ${me.data.school.name}` : ''}</p>
          </div>
          {showPin && <span className={`hidden sm:inline text-[11px] font-bold px-2.5 py-1 rounded-full ${me.data?.flags?.pin_set ? 'bg-green-500/20 text-green-200' : 'bg-amber-500/20 text-amber-200'}`}>{me.data?.flags?.pin_set ? 'PIN set' : 'No PIN yet'}</span>}
        </div>
      )}
      <div className="flex gap-1 bg-slate-100 rounded-2xl p-1 overflow-x-auto">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`flex-1 px-3 py-2 rounded-xl text-sm font-semibold whitespace-nowrap ${tab === k ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>{l}</button>)}
      </div>
      {msg && <Banner tone={msg.tone}>{msg.text}</Banner>}
      <QueryState q={me}>
        {me.data?.user && (
          <>
            {tab === 'profile' && <Profile me={me.data} setMsg={setMsg} />}
            {tab === 'security' && <><PasswordCard setMsg={setMsg} /><FingerprintCard setMsg={setMsg} name={useAuth.getState().user?.name ?? ''} role={useAuth.getState().user?.role} />{showPin && <Pin pinSet={!!me.data.flags?.pin_set} setMsg={setMsg} />}</>}
            {tab === 'alerts' && <Preferences setMsg={setMsg} />}
            {tab === 'devices' && <Devices setMsg={setMsg} />}
            {tab === 'profile' && extra}
          </>
        )}
      </QueryState>
    </div>
  );
}
